export type SourceType = 'channel' | 'group' | 'bot'

export type OnboardingStep =
  | 'welcome-intro'
  | 'overview'
  | 'source'
  | 'connect-subscriber'
  | 'connect-admin'
  | 'connect-done'
  | 'branch'
  | 'showcase-consent'
  | 'showcase-rules'
  | 'yandex-connect'
  | 'yandex-done'
  | 'requisites'
  | 'ad-gen-prepare'
  | 'creatives-editor'
  | 'campaigns-ready'
  | 'tariff'
  | 'final'

export type BranchPath = 'ads' | 'quick' | 'analytics' | 'showcase'

export type ConnectionState =
  | 'pending'
  | 'bot_subscribed'
  | 'bot_admin'
  | 'rights_partial'
  | 'rights_ok'
  | 'expired'

export interface OnboardingChannel {
  uuid: string
  title: string
  memberCount: number
  avatarUrl?: string
}

/**
 * B contract 2026-07-28: `optional` теперь nested `{granted, missing}` —
 * B знает какие optional-права юзер выдал (granted) и какие ещё нет (missing).
 * F показывает опциональные в MAX-mock как «можно оставить» с текущим состоянием toggle.
 *
 * REQUIRED = [write, edit, delete, pin_message, add_remove_members] (5).
 * OPTIONAL = [change_chat_info] (1). `add_admins` убран (MAX API не отдаёт).
 * `read_all_messages` тоже убран — всегда true у любого бота-админа.
 */
export interface OnboardingRights {
  granted: string[]
  missing: string[]
  required: string[]
  optional?: {
    granted: string[]
    missing: string[]
  }
}

export interface OnboardingState {
  currentStep: OnboardingStep | null
  branchPath: BranchPath | null
  channelUUID?: string
  hasYandexAuth: boolean
  hasCampaigns: boolean
  hasTariff: boolean
}

export interface InitOnboardingResponse {
  onboardToken: string
  maxDeepLink: string
  expiresAt: string
}

export interface ConnectionStatusResponse {
  state: ConnectionState
  channel?: OnboardingChannel
  rights?: OnboardingRights
  expiresAt: string
}

const USE_MOCKS = false

let mockToken = ''
let mockTransitionAt = 0
const mockState: OnboardingState = {
  currentStep: null,
  branchPath: null,
  hasYandexAuth: false,
  hasCampaigns: false,
  hasTariff: false,
}

function tickMockConnection(): ConnectionState {
  if (mockTransitionAt === 0) return 'pending'
  const elapsed = Date.now() - mockTransitionAt
  if (elapsed < 4000) return 'pending'
  if (elapsed < 8000) return 'bot_admin'
  if (elapsed < 12000) return 'rights_partial'
  return 'rights_ok'
}

export async function initOnboarding(sourceType: SourceType): Promise<InitOnboardingResponse> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 200))
    if (sourceType !== 'channel') throw new Error('INVALID_SOURCE_TYPE')
    mockToken = `mock-${Math.random().toString(36).slice(2, 10)}`
    mockTransitionAt = Date.now()
    return {
      onboardToken: mockToken,
      maxDeepLink: `https://max.ru/?startapp=onboard_${mockToken}`,
      expiresAt: new Date(Date.now() + 30 * 60_000).toISOString(),
    }
  }
  const res = await fetch('/api/max/onboarding/init', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceType }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

export async function getConnectionStatus(token: string): Promise<ConnectionStatusResponse> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 150))
    const state = tickMockConnection()
    const expiresAt = new Date(Date.now() + 25 * 60_000).toISOString()
    if (state === 'pending') return { state, expiresAt }
    const channel: OnboardingChannel = {
      uuid: '0322ef44-9ad1-4100-867f-bab2351cc622',
      title: 'Кофейный канал',
      memberCount: 5132,
    }
    if (state === 'bot_admin' || state === 'rights_partial') {
      // Mock под B contract 2026-07-28: required=5, optional={granted, missing}.
      return {
        state,
        channel,
        rights: {
          granted: state === 'bot_admin'
            ? ['write', 'edit', 'pin_message']
            : ['write', 'edit'],
          missing: state === 'bot_admin'
            ? ['delete', 'add_remove_members']
            : ['delete', 'pin_message', 'add_remove_members'],
          required: ['write', 'edit', 'delete', 'pin_message', 'add_remove_members'],
          optional: { granted: [], missing: ['change_chat_info'] },
        },
        expiresAt,
      }
    }
    return {
      state: 'rights_ok',
      channel,
      rights: {
        granted: ['write', 'edit', 'delete', 'pin_message', 'add_remove_members'],
        missing: [],
        required: ['write', 'edit', 'delete', 'pin_message', 'add_remove_members'],
        optional: { granted: [], missing: ['change_chat_info'] },
      },
      expiresAt,
    }
  }
  const res = await fetch(`/api/max/onboarding/status/${encodeURIComponent(token)}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

export async function getOnboardingState(): Promise<OnboardingState> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 100))
    return { ...mockState }
  }
  const res = await fetch('/api/max/onboarding/state', { credentials: 'include' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

export async function patchOnboardingState(
  patch: Partial<Pick<OnboardingState, 'currentStep' | 'branchPath'>>,
): Promise<OnboardingState> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 80))
    if (patch.currentStep !== undefined) mockState.currentStep = patch.currentStep
    if (patch.branchPath !== undefined) mockState.branchPath = patch.branchPath
    return { ...mockState }
  }
  const res = await fetch('/api/max/onboarding/state', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  // Бэк может вернуть 204 No Content — не пытаемся парсить пустое тело
  if (res.status === 204) return getOnboardingState()
  const text = await res.text()
  if (!text) return getOnboardingState()
  try {
    return JSON.parse(text)
  } catch {
    return getOnboardingState()
  }
}

export function bookmarkStep(step: OnboardingStep | null, branchPath?: BranchPath | null): void {
  const patch: Partial<Pick<OnboardingState, 'currentStep' | 'branchPath'>> = { currentStep: step }
  if (branchPath !== undefined) patch.branchPath = branchPath
  patchOnboardingState(patch).catch((e) => {
    console.warn('[onboarding] bookmarkStep failed', e)
  })
}

export async function cancelOnboarding(): Promise<void> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 100))
    mockState.currentStep = null
    mockState.branchPath = null
    return
  }
  const res = await fetch('/api/max/onboarding/cancel', {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`)
}

export async function recheckRights(channelUUID: string): Promise<{ rights: OnboardingRights }> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 300))
    return {
      rights: {
        granted: ['write', 'edit', 'pin_message'],
        missing: ['delete', 'add_remove_members'],
        required: ['write', 'edit', 'delete', 'pin_message', 'add_remove_members'],
        optional: { granted: [], missing: ['change_chat_info'] },
      },
    }
  }
  const res = await fetch(`/api/max/onboarding/check-rights/${encodeURIComponent(channelUUID)}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

/**
 * B contract 2026-07-28: 5 required + 1 optional. `add_admins` и
 * `read_all_messages` больше не возвращаются (см. OnboardingRights).
 * Legacy-mapping оставлен на случай если бэк вернёт их старую версию —
 * label не будет `«unknown»` в UI.
 */
export const RIGHT_LABELS: Record<string, string> = {
  write: 'Писать посты',
  edit: 'Редактировать чужие посты',
  delete: 'Удалять чужие посты',
  pin_message: 'Закреплять посты',
  add_remove_members: 'Добавлять и удалять подписчиков',
  change_chat_info: 'Редактировать канал (фото, название, описание)',
  // legacy fallback (не в required/optional новой версии, но mapping держим):
  read_all_messages: 'Чтение постов канала',
  add_admins: 'Назначение администраторов',
}

export function rightLabel(key: string): string {
  return RIGHT_LABELS[key] || `«${key}»`
}

export const STEP_TO_PROGRESS: Record<OnboardingStep, number> = {
  'welcome-intro': 0,
  overview: 1,
  source: 2,
  'connect-subscriber': 2,
  'connect-admin': 2,
  'connect-done': 2,
  branch: 3, // шаг пропущен в потоке v1 — оставлен на будущее
  'showcase-consent': 4,
  'showcase-rules': 4,
  'yandex-connect': 3,
  'yandex-done': 4,
  requisites: 4,
  tariff: 5,
  'ad-gen-prepare': 6,
  'creatives-editor': 6,
  'campaigns-ready': 7,
  final: 8,
}

export const STEP_TO_ROUTE: Record<OnboardingStep, string> = {
  'welcome-intro': '/onboarding',
  overview: '/onboarding/overview',
  source: '/onboarding/source',
  'connect-subscriber': '/onboarding/connect-subscriber',
  'connect-admin': '/onboarding/connect-admin',
  'connect-done': '/onboarding/connect-done',
  branch: '/onboarding/branch',
  'showcase-consent': '/onboarding/showcase-consent',
  'showcase-rules': '/onboarding/showcase-rules',
  'yandex-connect': '/onboarding/yandex-connect',
  'yandex-done': '/onboarding/yandex-done',
  requisites: '/onboarding/requisites',
  'ad-gen-prepare': '/onboarding/ad-gen-prepare',
  'creatives-editor': '/onboarding/creatives-editor',
  'campaigns-ready': '/onboarding/campaigns-ready',
  tariff: '/onboarding/tariff',
  final: '/onboarding/final',
}
